import React, {useEffect, useState} from 'react';
import {AnimatePresence, motion} from 'framer-motion';

export type ToastType = 'success' | 'error' | 'warning';

interface ToastProps {
    show: boolean;
    setShow: (show: boolean) => void;
    message: string;
    type: ToastType;
}

const TOAST_DURATION = 4000;

const TOAST_CONFIG: Record<ToastType, { color: string; icon: string; borderColor: string }> = {
    success: {
        color: '#2ad167',
        icon: 'bi-check-lg',
        borderColor: '#2ad167',
    },
    error: {
        color: '#ef4444',
        icon: 'bi-x-circle',
        borderColor: '#ef4444',
    },
    warning: {
        color: '#f59e0c',
        icon: 'bi-exclamation-triangle',
        borderColor: '#f59e0c',
    },
};

export default function Toast(props: ToastProps) {
    const {show, setShow, message, type} = props;
    const [progress, setProgress] = useState(100);
    const config = TOAST_CONFIG[type];

    useEffect(() => {
        if (show) {
            setProgress(100);
            const startTime = Date.now();
            const interval = setInterval(() => {
                const elapsed = Date.now() - startTime;
                const remaining = Math.max(0, 100 - (elapsed / TOAST_DURATION) * 100);
                setProgress(remaining);
                if (remaining <= 0) {
                    clearInterval(interval);
                }
            }, 30);
            const timer = setTimeout(() => setShow(false), TOAST_DURATION);
            return () => {
                clearTimeout(timer);
                clearInterval(interval);
            };
        }
    }, [show, setShow]);

    return (
        <AnimatePresence>
            {show && (
                <motion.div
                    className="my-toast"
                    role="alert"
                    aria-live="assertive"
                    aria-atomic="true"
                    initial={{opacity: 0, x: 80, scale: 0.95}}
                    animate={{opacity: 1, x: 0, scale: 1}}
                    exit={{opacity: 0, x: 80, scale: 0.95}}
                    transition={{duration: 0.3, ease: [0.25, 0.1, 0.25, 1]}}
                    style={{borderLeftColor: config.borderColor}}
                >
                    <div className="d-flex align-items-center gap-3 px-4 py-3">
                        <div
                            className="d-flex align-items-center justify-content-center flex-shrink-0"
                            style={{
                                width: '28px', height: '28px',
                                borderRadius: '8px',
                                background: `${config.color}26`,
                                border: `1px solid ${config.color}40`,
                            }}
                        >
                            <i className={`bi ${config.icon}`} style={{fontSize: '0.9rem', color: config.color}}></i>
                        </div>
                        <span className="text-white" style={{fontSize: '0.88rem', lineHeight: 1.4}}>
                            {message}
                        </span>
                        <button
                            type="button"
                            className="btn-close btn-close-white ms-auto flex-shrink-0"
                            aria-label="Close"
                            onClick={() => setShow(false)}
                            style={{fontSize: '0.6rem', opacity: 0.5}}
                        ></button>
                    </div>
                    <div style={{
                        position: 'absolute',
                        bottom: 0,
                        left: 0,
                        height: '2px',
                        width: `${progress}%`,
                        background: `linear-gradient(90deg, ${config.color} 0%, ${config.color}66 100%)`,
                        borderRadius: '0 0 0 16px',
                        transition: 'width 0.05s linear',
                    }}/>
                </motion.div>
            )}
        </AnimatePresence>
    );
}
